import { useMemo } from 'react'
import { twMerge } from 'tailwind-merge'
import { useReminderActions, type ReminderItem } from '../../../hooks/use-reminders'
import { ReminderThumbnail } from '../reminders-inline-image-preview'
import { ReminderLinkAttachments } from '../reminders-link-attachment'
import { extractUrls } from '../utils/extract-urls'
import type { PendingImage } from '../utils/image-attachments'

interface ReminderAttachmentsProps {
  item: ReminderItem
  /** After a removal the host wants to flash as saved. */
  onSaving?: () => void
  /** Images picked and still uploading, shown ahead of the saved ones. */
  pending?: Array<PendingImage>
  className?: string
}

/**
 * What a reminder carries: the links in its title as favicon pills, and its
 * images as thumbnails. Shared by the row's popover and the Reminder View, so
 * a reminder shows the same things on either.
 */
export function ReminderAttachments({ item, onSaving, pending = [], className }: ReminderAttachmentsProps) {
  const { removeAttachment } = useReminderActions()
  const urls = useMemo(() => extractUrls(item.title), [item.title])
  const attachments = item.attachments ?? []

  if (urls.length === 0 && attachments.length === 0 && pending.length === 0) return null

  return (
    <div className={twMerge('flex shrink-0 flex-col gap-1', className)}>
      {urls.length > 0 && (
        <div className="flex shrink-0 flex-wrap gap-1">
          <ReminderLinkAttachments urls={urls} />
        </div>
      )}
      {(attachments.length > 0 || pending.length > 0) && (
        <div className="flex shrink-0 flex-wrap gap-1">
          {attachments.map((fileId) => (
            <ReminderThumbnail
              key={fileId}
              fileId={fileId}
              onRemove={() => {
                removeAttachment(item.id, fileId)
                onSaving?.()
              }}
            />
          ))}
          {pending.map((image) => (
            <ReminderThumbnail key={image.localId} src={image.previewUrl} uploading />
          ))}
        </div>
      )}
    </div>
  )
}
